import { capitalizeString } from '@blisspointmedia/bpm-types/dist/StyleUtils';
import * as R from 'ramda';

export const DATE_FORMAT = 'yyyy-MM-dd';

export const toFloat = (val: string | number): number =>
  typeof val === 'number' ? val : parseFloat(val);

export type MemoizedGetter<Input, Output, Args extends any[] = []> = (
  input: Input,
  ...agrs: Args
) => Output;
export const makeMemoizedGetter = <Input, Output, Args extends any[] = []>({
  calculate,
  makeKey = (val: Input, ...args: Args) => JSON.stringify(val, ...args)
}: {
  calculate: (val: Input, ...args: Args) => Output;
  makeKey?: (val: Input, ...args: Args) => string;
}): MemoizedGetter<Input, Output, Args> => {
  const table: Record<string, Output> = {};
  return (val, ...args: Args) => {
    const key = makeKey(val, ...args);
    if (Object.hasOwn(table, key)) {
      return table[key];
    }
    const calculated = calculate(val, ...args);
    table[key] = calculated;
    return calculated;
  };
};

/**
 *  Function to shorten numbers for the y-axis
 *  It is also used in the Reporting.tsx file to shorten file sizes
 *  Will try and bucket the number into a denom (e.g 'k', 'm', 'g', 't')
 *  finds the appropriate bucket by looking at the number length and then
 *  dividing it by three in order to place it an index, that index value is then used to trim the
 *  number from least-to-most significant figures (e.g. 250,000 will become 250k and so)
 * @param {number} value
 * @returns {string} newValue
 */
export const abbreviateNumber = (value: number): string | number => {
  let newValue: string | number = value;
  if (value >= 1000) {
    const suffixes = ['', 'k', 'm', 'g', 't'];
    // Tier by the integer part's digit count, not the full string — a decimal value like
    // 4070054.55 has more characters than its magnitude warrants, which previously inflated
    // the tier (millions showing as "4.1g" instead of "4.1m").
    const suffixNum = Math.floor((`${Math.floor(value)}`.length - 1) / 3);
    const short = (value / 1000 ** suffixNum).toFixed(1);
    newValue = `${short}${suffixes[suffixNum]}`;
  }

  return newValue;
};

export const parseInputToInt = (input = ''): number =>
  parseInt(input.replace(/[^\d.]/g, ''), 10) || 0;
export const parseInputToFloat = (input = ''): number =>
  parseFloat(input.replace(/[^\d.]/g, '')) || 0;

export const mergeNumberObjects = R.mergeDeepWith((a: number, b: number) => a + b);

const lowerCaseWords = new Set([
  // Articles
  'a',
  'an',
  'the',
  // Coordinating Conjunctions
  'and',
  'but',
  'or',
  'nor',
  'for',
  'yet',
  'so',
  // Prepositions
  'at',
  'by',
  'in',
  'of',
  'on',
  'to',
  'up',
  'as',
  'off',
  'out',
  'via',
  'per',
  're',
  'into',
  'onto',
  'upon',
  'with',
  'from',
  'over',
  'than',
  'that',
  'then',
  'till',
  'until',
  'unto',
  'down',
  'near',
  'past',
  'plus',
  'like',
  'amid',
  'among',
  'about',
  'above',
  'after',
  'along',
  'below',
  'under',
  'since',
  'while',
  'within',
  'around',
  'before',
  'behind',
  'beside',
  'except',
  'across',
  'against',
  'beyond',
  'during',
  'inside',
  'outside',
  'through',
  'between',
  'without',
  'despite',
  'towards',
  'whether',
  'throughout',
  'underneath',
  'alongside'
]);

export const snakeCaseToCapitalized = (string: string): string => {
  const capitalized = string
    .split('_')
    .map((word, i) => {
      if (i > 0 && lowerCaseWords.has(word.toLowerCase())) {
        return word;
      }
      return capitalizeString(word);
    })
    .join(' ');

  return capitalized;
};

// Canonicalize a value for a stable, order-independent structural comparison: recursively sort object
// keys and drop `undefined`-valued keys (so `{ a: undefined }` equals `{}`), leaving array order
// intact. Serialize the result (e.g. `JSON.stringify(canonicalize(x))`) to compare two values without
// false positives from key order or absent-vs-undefined.
export const canonicalize = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        const v = (value as Record<string, unknown>)[key];
        if (v !== undefined) {
          acc[key] = canonicalize(v);
        }
        return acc;
      }, {});
  }
  return value;
};
